import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import cx from 'classnames2';
import CircularProgress from '@mui/material/CircularProgress';
import { ADVANCED, ANALYTICS_DATE_RANGE_OPTIONS } from '@opengov/stories-shared/constants';
import { IStory } from '@opengov/stories-shared/types';
import { ISettings, IStoryAnalytics, IStoryAnalyticsPayload, IStoryListMessages } from '../../../../types';
import { StoryRow } from '../StoryRow';
import { GoogleSignInButton } from './GoogleSignInButton/GoogleSignInButton';
import styles from './Analytics.scss';

export interface IAnalyticsProps {
  stories: IStory[];
  messages: IStoryListMessages;
  analytics: IStoryAnalytics;
  settings: ISettings;
  isAnalyticsLoading: boolean;
  toggleAnalyticsLoading(loading: boolean): any;
  setAnalyticsAuthHeader(props: { authHeader: string }): any;
  getAnalyticsData(props: IStoryAnalyticsPayload): any;
  getAnalyticsProperty(props: { authHeader: string; google_analytics_id: string }): any;
  handleStoryClick(story: IStory, isTemplate: boolean): any;
  navigateToSettings(tab?: string): any;
}

export const dateRangeToLabel = (rangeId: string): string => {
  switch (rangeId) {
    case 'month':
      return 'Days';
    case 'year':
      return 'Months';
    case 'week':
      return 'Day';
    case 'day':
      return 'Hours';
    default:
      return 'unknown';
  }
};

export const Analytics: React.FC<IAnalyticsProps> = (props) => {
  const {
    stories,
    messages,
    analytics,
    settings,
    isAnalyticsLoading,
    toggleAnalyticsLoading,
    setAnalyticsAuthHeader,
    getAnalyticsData,
    getAnalyticsProperty,
    handleStoryClick,
    navigateToSettings
  } = props;
  const [currentDateRange, setCurrentDateRange] = useState(ANALYTICS_DATE_RANGE_OPTIONS[0]);
  const [currentPagePaths, setCurrentPagePaths] = useState([]);
  const [noViewError, setNoViewError] = useState(false);

  const createDateRangeObject = (options: any = {}) => {
    const currentDate = new Date();
    const currentTimeArray = [currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, currentDate.getUTCDate()];

    let startDate: Date;

    switch (currentDateRange.id) {
      case 'year':
        startDate = new Date(new Date().setUTCMonth(currentDate.getUTCMonth() - 12));
        break;
      case 'week':
        startDate = new Date(new Date().setUTCDate(currentDate.getUTCDate() - 7));
        break;
      case 'day':
        startDate = new Date(new Date().setUTCDate(currentDate.getUTCDate() - 1));
        break;
      case 'month':
      default:
        startDate = new Date(new Date().setUTCMonth(currentDate.getUTCMonth() - 1));
        break;
    }

    const startTimeArray = [startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, startDate.getUTCDate()];

    const formatDatePartial = (d) => {
      const str = `${d}`;
      return str.length < 2 ? `0${str}` : str;
    };

    let startDateString = startTimeArray.map(formatDatePartial).join('-');

    if (options.allTime) startDateString = '2005-01-01';

    return {
      startDate: startDateString,
      endDate: currentTimeArray.map(formatDatePartial).join('-')
    };
  };

  const getAnalyticsView = () => {
    const dateRange = createDateRangeObject();

    getAnalyticsData({
      propertyId: analytics.propertyId,
      authHeader: analytics.authHeader,
      dateRange,
      metrics: [
        { name: 'screenPageViews' },
        { name: 'screenPageViewsPerUser' },
        { name: 'userEngagementDuration' },
        { name: 'eventCount' }
      ],
      dimensions: [{ name: 'pagePathPlusQueryString' }]
    });
  };

  useEffect(() => {
    if (!analytics.authHeader) {
      toggleAnalyticsLoading(false);
    } else if (!noViewError && analytics.authHeader && (!analytics.propertyId || analytics.propertyId.trim() === '')) {
      toggleAnalyticsLoading(true);
      getAnalyticsProperty({
        authHeader: analytics.authHeader,
        google_analytics_id: settings.advanced.google_analytics_id
      }).then((property) => {
        if (property === null) {
          toggleAnalyticsLoading(false);
          setNoViewError(true);
        }
      });
    } else if (analytics.authHeader && analytics.propertyId) {
      toggleAnalyticsLoading(false);
    }
  }, [analytics.authHeader, analytics.propertyId]);

  useEffect(() => {
    if (!!analytics.propertyId && currentDateRange) {
      toggleAnalyticsLoading(true);
      getAnalyticsView();
      toggleAnalyticsLoading(false);
    }
  }, [currentDateRange, analytics.propertyId]);

  const handleCredentialResponse = (response) => {
    setAnalyticsAuthHeader({
      authHeader: `${response.token_type} ${response.access_token}`
    });
  };

  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch (e) {
      return false;
    }
  };

  const onDateRangeChange = (val) => {
    setCurrentDateRange(val);
  };

  const onPagePathStateChange = (fullUrl) => {
    let newPathArray = [...currentPagePaths];

    if (!fullUrl) {
      newPathArray = [];
    } else {
      if (isValidUrl(fullUrl)) {
        const url = new URL(fullUrl);
        const existingItemIndex = newPathArray.indexOf(url.pathname);

        if (existingItemIndex > -1) {
          newPathArray.splice(existingItemIndex, 1);
        } else {
          newPathArray.push(url.pathname);
        }
      }
    }

    setCurrentPagePaths(newPathArray);
  };

  const isCheckboxChecked = (fullUrl) => {
    const url = new URL(fullUrl);
    return currentPagePaths.indexOf(url.pathname) > -1;
  };

  const formatTime = (seconds) => {
    const int = parseInt(seconds, 10);
    return int > 120 ? `${Math.ceil(int / 60)}m` : `${Math.ceil(int)}s`;
  };

  const getAnalyticsDataForStoryListItem = (fullUrl) => {
    if (!analytics.data.rows || analytics.data.rows.length === 0) {
      return {
        pageViews: '0',
        uniquePageViews: '0',
        averageTime: '0s',
        shares: '0'
      };
    }

    const url = new URL(fullUrl);
    const matchingRow = analytics.data.rows.find((row) => row.dimensionValues[0].value === url.pathname);

    if (!matchingRow) {
      return {
        pageViews: '0',
        uniquePageViews: '0',
        averageTime: '0s',
        shares: '0'
      };
    }

    const [pageViewsValue, uniquePageViewsValue, averageTimeValue, sharesValue] = matchingRow.metricValues.map(
      (metric) => metric.value
    );

    return {
      pageViews: pageViewsValue || '0',
      uniquePageViews: parseFloat(uniquePageViewsValue).toFixed(2) || '0',
      averageTime: formatTime(averageTimeValue || '0'),
      shares: sharesValue || '0'
    };
  };

  const renderStoryList = () => {
    const isTemplate = false;

    const storiesWithValidPublicUrls = stories.filter((story) => isValidUrl(story.public_url));
    return storiesWithValidPublicUrls.map((story) => (
      <StoryRow
        key={`story-${story._id}`}
        messages={messages}
        onClick={() => handleStoryClick(story, isTemplate)}
        displayCheckBox={false}
        onCheckBoxClick={() => onPagePathStateChange(story.public_url)}
        isCheckboxChecked={isCheckboxChecked(story.public_url)}
        displayAnalytics={true}
        analyticsData={getAnalyticsDataForStoryListItem(story.public_url)}
        data-test="story-row"
        {...story}
      />
    ));
  };

  const renderChildren = () => {
    if (isAnalyticsLoading) {
      return (
        <section>
          <CircularProgress />
        </section>
      );
    } else if (!analytics.authHeader) {
      return <GoogleSignInButton messages={messages} handleCredentialResponse={handleCredentialResponse} />;
    } else if (noViewError) {
      return (
        <section className={styles.errorWrapper}>
          <h2>{messages.analyticsErrorHeader}</h2>
          <p>
            {messages.analyticsErrorBody}&nbsp;
            <button className={styles.settingsLink} onClick={() => navigateToSettings(ADVANCED)}>
              {messages.analyticsErrorLink}
            </button>
          </p>
        </section>
      );
    } else if (!!analytics.data && !!analytics.propertyId) {
      return (
        <section className={styles.selectorsSection}>
          <div className="ui-row">
            <div className={cx(styles.selectorWrapper, 'ui-col-3of6')}>
              <fieldset className="ui-form required">
                <Select
                  clearable={false}
                  className="date-range-selector"
                  name="updateDateRange"
                  labelKey="name"
                  valueKey="id"
                  options={ANALYTICS_DATE_RANGE_OPTIONS}
                  onChange={onDateRangeChange}
                  value={currentDateRange}
                  resetValue={{ id: null, name: '' }}
                />
              </fieldset>
            </div>
          </div>

          <div className="ui-table">
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className="no-sort">{messages.image}</th>
                  <th className="no-sort">{messages.pageName}</th>
                  <th className="no-sort">{messages.createdDate}</th>
                  <th className="no-sort">{messages.createdBy}</th>
                  <th className="no-sort">{messages.views}</th>
                  <th className="no-sort">{messages.uniquePageViews}</th>
                  <th className="no-sort">{messages.averageTime}</th>
                  <th className="no-sort">{messages.shares}</th>
                  <th className="no-sort" />
                </tr>
              </thead>
              <tbody>{renderStoryList()}</tbody>
            </table>
          </div>
        </section>
      );
    }
  };

  return <div className={styles.root}>{renderChildren()}</div>;
};
